/**
 * Generate a device fingerprint for trial abuse prevention
 */
export const generateDeviceFingerprint = (): string => {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	// Canvas fingerprint
	let canvasFingerprint = "";
	if (ctx) {
		ctx.textBaseline = "top";
		ctx.font = "14px Arial";
		ctx.fillText("Device fingerprint test 🔒", 2, 2);
		canvasFingerprint = canvas.toDataURL();
	}

	// Screen properties
	const screen = {
		width: window.screen.width,
		height: window.screen.height,
		colorDepth: window.screen.colorDepth,
		pixelDepth: window.screen.pixelDepth,
		availWidth: window.screen.availWidth,
		availHeight: window.screen.availHeight,
	};

	// Navigator properties
	const navigator = {
		userAgent: window.navigator.userAgent,
		language: window.navigator.language,
		languages: window.navigator.languages?.join(",") || "",
		platform: window.navigator.platform,
		cookieEnabled: window.navigator.cookieEnabled,
		doNotTrack: window.navigator.doNotTrack,
		hardwareConcurrency: (window.navigator as any).hardwareConcurrency || 0,
		maxTouchPoints: (window.navigator as any).maxTouchPoints || 0,
	};

	// Timezone
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	// WebGL fingerprint
	let webglFingerprint = "";
	try {
		const gl =
			canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
		if (gl && "getExtension" in gl) {
			const debugInfo = (gl as WebGLRenderingContext).getExtension(
				"WEBGL_debug_renderer_info",
			);
			if (debugInfo) {
				webglFingerprint =
					(gl as WebGLRenderingContext).getParameter(
						debugInfo.UNMASKED_VENDOR_WEBGL,
					) +
					(gl as WebGLRenderingContext).getParameter(
						debugInfo.UNMASKED_RENDERER_WEBGL,
					);
			}
		}
	} catch (e) {
		// WebGL not available
	}

	// Audio context fingerprint
	let audioFingerprint = "";
	try {
		const audioCtx = new (
			window.AudioContext || (window as any).webkitAudioContext
		)();
		const oscillator = audioCtx.createOscillator();
		const analyser = audioCtx.createAnalyser();
		const gain = audioCtx.createGain();
		const scriptProcessor = audioCtx.createScriptProcessor(4096, 1, 1);

		oscillator.type = "triangle";
		oscillator.frequency.value = 10000;

		gain.gain.value = 0;
		oscillator.connect(analyser);
		analyser.connect(scriptProcessor);
		scriptProcessor.connect(gain);
		gain.connect(audioCtx.destination);

		oscillator.start(0);

		// Get frequency data
		const freqData = new Uint8Array(analyser.frequencyBinCount);
		analyser.getByteFrequencyData(freqData);
		audioFingerprint = Array.from(freqData).slice(0, 10).join("");

		oscillator.stop();
		audioCtx.close();
	} catch (e) {
		// Audio context not available
	}

	// Combine all fingerprints
	const fingerprint = {
		canvas: canvasFingerprint.slice(-50), // Last 50 chars to keep it manageable
		screen,
		navigator,
		timezone,
		webgl: webglFingerprint,
		audio: audioFingerprint,
		timestamp: Date.now(),
	};

	// Create hash
	return btoa(JSON.stringify(fingerprint))
		.replace(/[^a-zA-Z0-9]/g, "")
		.substring(0, 64);
};

/**
 * Check if device fingerprinting is available
 */
export const isDeviceFingerprintingAvailable = (): boolean => {
	return (
		typeof window !== "undefined" &&
		typeof document !== "undefined" &&
		typeof window.screen !== "undefined"
	);
};
